import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../api/client";
import { useAuth } from "./AuthContext";
import logger from "../utils/logger.js";

const FavoritesContext = createContext(null);

const STORAGE_KEY = "favourites";
const FAVORITES_ENDPOINT = "favorites";
const FAVORITES_REFRESH_INTERVAL_MS = 15000;
const FAVORITES_ADD_MUTATIONS = [
  { endpoint: FAVORITES_ENDPOINT, method: "post", body: (productId) => ({ product_id: productId }) },
  { endpoint: `${FAVORITES_ENDPOINT}/add`, method: "post", body: (productId) => ({ product_id: productId }) },
];
const FAVORITES_REMOVE_MUTATIONS = [
  { endpoint: (productId) => `${FAVORITES_ENDPOINT}/${productId}`, method: "delete" },
  { endpoint: `${FAVORITES_ENDPOINT}/remove`, method: "post", body: (productId) => ({ product_id: productId }) },
];

function toId(value) {
  if (value === null || value === undefined) return null;
  return String(value);
}

function isNotFoundError(error) {
  return error?.status === 404 || error?.status === 405;
}

function normalizeFavoriteItem(item) {
  if (typeof item === "string" || typeof item === "number") {
    const id = toId(item);
    if (!id) return null;

    return {
      id,
      name: "",
      price: 0,
      image: "",
      added_at: null,
      raw: item,
    };
  }

  if (!item || typeof item !== "object") return null;

  const source = item.product && typeof item.product === "object" ? item.product : item;
  const productId = source.id ?? source.product_id ?? item.product_id;
  const id = toId(productId);

  if (!id) return null;

  return {
    id,
    name:
      source.name ||
      source.name_ar ||
      source.name_en ||
      item.name ||
      item.title ||
      "",
    price: Number(
      source.sale_price ??
        source.price ??
        item.price ??
        0
    ),
    image:
      source.image_url ||
      source.image ||
      item.image_url ||
      item.image ||
      "",
    added_at: item.added_at || item.created_at || source.created_at || null,
    raw: item,
  };
}

function extractFavoritesList(payload) {
  const candidates = [
    payload?.favorites,
    payload?.favourites,
    payload?.wishlist,
    payload?.items,
    payload?.data?.favorites,
    payload?.data?.favourites,
    payload?.data?.wishlist,
    payload?.data?.items,
    payload?.data,
    payload?.user?.favorites,
    payload?.user?.favourites,
    payload?.user?.wishlist,
  ];

  const list = candidates.find(Array.isArray);
  if (!Array.isArray(list)) return null;

  return list.map(normalizeFavoriteItem).filter(Boolean);
}

function readFavoritesFromStorage(storageKey) {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || "[]");
    if (!Array.isArray(stored)) return [];
    return stored.map(normalizeFavoriteItem).filter(Boolean);
  } catch {
    return [];
  }
}

function getStorageKey(userId) {
  return userId ? `${STORAGE_KEY}:user:${userId}` : STORAGE_KEY;
}

function sameFavorites(a, b) {
  if (a.length !== b.length) return false;
  const aIds = a.map((item) => item.id).sort();
  const bIds = b.map((item) => item.id).sort();
  return aIds.every((id, index) => id === bIds[index]);
}

async function runMutation(config, productId) {
  const endpoint =
    typeof config.endpoint === "function" ? config.endpoint(productId) : config.endpoint;

  if (config.method === "delete") {
    return api.apiDelete(endpoint);
  }

  return api.apiPost(endpoint, config.body ? config.body(productId) : {});
}

export function FavoritesProvider({ children }) {
  const { user, isLogged } = useAuth();
  const userId = user?.id ?? null;
  const storageKey = useMemo(() => getStorageKey(userId), [userId]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncAvailable, setSyncAvailable] = useState(false);
  const syncAvailableRef = useRef(false);

  const saveFavoritesLocally = useCallback(
    (nextFavorites) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(nextFavorites));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextFavorites));
      } catch {}
    },
    [storageKey]
  );

  const applyFavorites = useCallback(
    (nextFavorites) => {
      setFavorites(nextFavorites);
      saveFavoritesLocally(nextFavorites);
      window.dispatchEvent(
        new CustomEvent("favourites-updated", {
          detail: { items: nextFavorites, count: nextFavorites.length },
        })
      );
    },
    [saveFavoritesLocally]
  );

  const fetchRemoteFavorites = useCallback(async () => {
    try {
      const response = await api.apiGet(FAVORITES_ENDPOINT);
      const remoteFavorites = extractFavoritesList(response);

      if (remoteFavorites) {
        setSyncAvailable(true);
        syncAvailableRef.current = true;
        return remoteFavorites;
      }
    } catch (error) {
      if (!isNotFoundError(error)) {
        throw error;
      }
    }

    const profileFavorites = extractFavoritesList({ user });
    if (profileFavorites) {
      setSyncAvailable(true);
      syncAvailableRef.current = true;
      return profileFavorites;
    }

    setSyncAvailable(false);
    syncAvailableRef.current = false;
    return null;
  }, [user]);

  const pushMissingFavoritesToServer = useCallback(
    async (localFavorites, remoteFavorites) => {
      const remoteIds = new Set(remoteFavorites.map((item) => item.id));
      const missingItems = localFavorites.filter((item) => !remoteIds.has(item.id));

      if (missingItems.length === 0) return remoteFavorites;

      const mergedFavorites = [...remoteFavorites];

      for (const item of missingItems) {
        let synced = false;

        for (const mutation of FAVORITES_ADD_MUTATIONS) {
          try {
            await runMutation(mutation, item.id);
            synced = true;
            break;
          } catch (error) {
            if (!isNotFoundError(error)) {
              throw error;
            }
          }
        }

        if (synced) {
          mergedFavorites.push(item);
          setSyncAvailable(true);
          syncAvailableRef.current = true;
        }
      }

      return mergedFavorites;
    },
    []
  );

  const refreshFavorites = useCallback(async () => {
    const localFavorites = readFavoritesFromStorage(storageKey);

    if (!isLogged) {
      applyFavorites(localFavorites);
      setSyncAvailable(false);
      syncAvailableRef.current = false;
      setLoading(false);
      return localFavorites;
    }

    setLoading(true);

    try {
      const remoteFavorites = await fetchRemoteFavorites();

      if (remoteFavorites) {
        const mergedFavorites = await pushMissingFavoritesToServer(
          localFavorites,
          remoteFavorites
        );
        applyFavorites(mergedFavorites);
        return mergedFavorites;
      }

      applyFavorites(localFavorites);
      return localFavorites;
    } catch (error) {
      logger.warn("Favorites sync failed, falling back to local cache:", error);
      applyFavorites(localFavorites);
      return localFavorites;
    } finally {
      setLoading(false);
    }
  }, [
    applyFavorites,
    fetchRemoteFavorites,
    isLogged,
    pushMissingFavoritesToServer,
    storageKey,
  ]);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== storageKey && event.key !== STORAGE_KEY) return;

      const nextFavorites = readFavoritesFromStorage(storageKey);
      setFavorites((prev) => (sameFavorites(prev, nextFavorites) ? prev : nextFavorites));
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && isLogged) {
        refreshFavorites().catch(() => {});
      }
    };

    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isLogged, refreshFavorites, storageKey]);

  useEffect(() => {
    if (!isLogged) return;

    const intervalId = window.setInterval(() => {
      refreshFavorites().catch(() => {});
    }, FAVORITES_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isLogged, refreshFavorites]);

  const isFavorite = useCallback(
    (productId) => favorites.some((item) => item.id === toId(productId)),
    [favorites]
  );

  const addFavorite = useCallback(
    async (product) => {
      const productId = toId(product?.id);
      if (!productId || isFavorite(productId)) return false;

      const nextItem = normalizeFavoriteItem({
        ...product,
        product_id: product?.id,
        added_at: new Date().toISOString(),
      });

      if (!nextItem) return false;

      const nextFavorites = [...favorites, nextItem];
      applyFavorites(nextFavorites);

      if (isLogged) {
        let synced = false;

        for (const mutation of FAVORITES_ADD_MUTATIONS) {
          try {
            await runMutation(mutation, productId);
            synced = true;
            setSyncAvailable(true);
            syncAvailableRef.current = true;
            break;
          } catch (error) {
            if (!isNotFoundError(error)) {
              throw error;
            }
          }
        }

        if (synced) {
          await refreshFavorites();
        }
      }

      window.dispatchEvent(
        new CustomEvent("favourites-updated", {
          detail: {
            action: "add",
            productId,
            productName: nextItem.name,
            synced: syncAvailableRef.current,
          },
        })
      );

      return true;
    },
    [applyFavorites, favorites, isFavorite, isLogged, refreshFavorites]
  );

  const removeFavorite = useCallback(
    async (productId) => {
      const normalizedId = toId(productId);
      if (!normalizedId) return false;

      const removedItem = favorites.find((item) => item.id === normalizedId) || null;
      const nextFavorites = favorites.filter((item) => item.id !== normalizedId);
      applyFavorites(nextFavorites);

      if (isLogged) {
        let synced = false;

        for (const mutation of FAVORITES_REMOVE_MUTATIONS) {
          try {
            await runMutation(mutation, normalizedId);
            synced = true;
            setSyncAvailable(true);
            syncAvailableRef.current = true;
            break;
          } catch (error) {
            if (!isNotFoundError(error)) {
              throw error;
            }
          }
        }

        if (synced) {
          await refreshFavorites();
        }
      }

      window.dispatchEvent(
        new CustomEvent("favourites-updated", {
          detail: {
            action: "remove",
            productId: normalizedId,
            productName: removedItem?.name || "",
            synced: syncAvailableRef.current,
          },
        })
      );

      return true;
    },
    [applyFavorites, favorites, isLogged, refreshFavorites]
  );

  const toggleFavorite = useCallback(
    async (product) => {
      const productId = toId(product?.id);
      if (!productId) return false;

      if (isFavorite(productId)) {
        return removeFavorite(productId);
      }

      return addFavorite(product);
    },
    [addFavorite, isFavorite, removeFavorite]
  );

  const value = useMemo(
    () => ({
      favorites,
      favoritesCount: favorites.length,
      loading,
      syncAvailable,
      isFavorite,
      addFavorite,
      removeFavorite,
      toggleFavorite,
      refreshFavorites,
    }),
    [
      addFavorite,
      favorites,
      isFavorite,
      loading,
      refreshFavorites,
      removeFavorite,
      syncAvailable,
      toggleFavorite,
    ]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
